/**
 * A custom domain is a User domain or an Organization domain.
 * User domain: maps/{id}.userId. Organization domain: maps/{id}.orgId.
 * orgId is the existing exact Organization name. Personal maps have no orgId,
 * so they stay off an Organization domain that shares the maps collection.
 * collection is the Firestore collection for that host. It defaults to `maps`.
 * A host that sets collection keeps sign-in and editing, and every published map
 * in that collection loads there. `/` on that host opens homeMapId, not My Maps.
 * A domain that does not set collection is view-only.
 * When a collection host also sets orgId, saving requires membership in that Organization.
 * homeMapId is the map opened when the URL has no map id.
 */
export type UserEmbedDomain = {
	userId: string;
	homeMapId: string;
	collection?: string;
};

export type OrganizationEmbedDomain = {
	orgId: string;
	homeMapId?: string;
	collection?: string;
};

export type EmbedDomain = UserEmbedDomain | OrganizationEmbedDomain;

export const domainUsers: Record<string, EmbedDomain> = {
	"map.jerseycitymarathon.com": {
		userId: "0pEaIYDsLSObWYeLPqJnUm2HsUc2",
		homeMapId: "jersey-city-marathon",
	},
	"kartor.jarvaventyr.se": {
		userId: "DCppcox6BoXcHhECu67GnBomlHX2",
		homeMapId: "salen",
	},
	"kartor.midnattsloppet.com": {
		collection: "midnattsloppet",
		orgId: "Midnattsloppet",
	},
	"midnattsloppet.anderswikstrom.se": {
		collection: "midnattsloppet",
		orgId: "Midnattsloppet",
	},
};

export function embedDomain(hostname: string): EmbedDomain | undefined {
	return domainUsers[hostName(hostname)];
}

/** Firestore collection used when a domain does not set one. */
export const MAPS_COLLECTION = "maps";

export function mapCollectionForHost(hostname: string): string {
	return embedDomain(hostname)?.collection || MAPS_COLLECTION;
}

/** A domain is view-only unless it names its own collection. */
export function isViewOnlyEmbedHost(hostname: string): boolean {
	const domain = embedDomain(hostname);
	return domain != undefined && !domain.collection;
}

/** Organization membership required to save on this host. Absent when saving is not organization-gated. */
export function requiredSaveOrganization(hostname: string): string | undefined {
	const domain = embedDomain(hostname);
	if (!domain?.collection || !("orgId" in domain) || !domain.orgId) return undefined;
	return domain.orgId;
}

/**
 * `/` on a collection host opens this path. Undefined when homeMapId is not set,
 * so the host stays on `/` instead of My Maps.
 */
export function collectionIndexPath(hostname: string): string | undefined {
	const domain = embedDomain(hostname);
	if (!domain?.collection || !domain.homeMapId) return undefined;
	return `/${domain.homeMapId}`;
}

function hostName(hostname: string): string {
	return hostname.toLowerCase().split(":")[0] ?? "";
}

/**
 * Public hosts and hosts with their own collection may load any published map in that collection.
 * A User domain on the maps collection loads maps with that userId.
 * An Organization domain on the maps collection loads maps with that orgId, and not personal maps.
 */
export function publishedMapVisibleOnHost(hostname: string, mapUserId: unknown, mapOrgId: unknown): boolean {
	const domain = embedDomain(hostname);
	if (!domain || domain.collection) return true;
	if ("orgId" in domain) return mapOrgId === domain.orgId;
	if ("userId" in domain) return mapUserId === domain.userId;
	return true;
}
